import {
	GET_PAYMENT_TOKEN_COMPLETED,
	GENERAL_ERROR,
	CARD_FORM_READY,
	CARD_FORM_ERROR,
} from '../Checkout/common.mjs';
import { registerPaymentProcessor } from '../Checkout/main.mjs';

let paymentMethod;

/**
 * @param {{staxNumberId: string, staxCvvId: string, method: string}} data The data
 * @param {object} options The options
 * @param {(key: string, value: any) => void} sendSub The send sub callback
 * @returns {Promise<{key: string, value?: any}>} A promise with the result
 */
export async function handleDomReady({ squareCardId, method }, options) {
	try {
		// @ts-ignore
		if (!window.Square) {
			throw new Error('Square.js failed to load properly');
		}

		const payments = window.Square.payments(
			options.squareAppId,
			options.squareLocationId,
		);

		if (method === 'bank') {
			paymentMethod = await payments.ach();
			return { key: CARD_FORM_READY };
		}

		let card;
		try {
			card = await payments.card();
			await card.attach(`#${squareCardId}`);

			paymentMethod = card;

			return { key: CARD_FORM_READY };
		} catch (e) {
			return Promise.reject({
				key: CARD_FORM_ERROR,
				value: e.message,
			});
		}
	} catch (error) {
		return Promise.reject({ key: GENERAL_ERROR, value: error });
	}
}

/**
 *
 * @param paymentData
 */
async function tokenizeAch(paymentData) {
	try {
		const promise = new Promise((resolve, reject) => {
			paymentMethod.addEventListener('ontokenization', async (event) => {
				const { tokenResult, error } = event.detail;
				if (error !== undefined) {
					const errorMessage = `Tokenization failed with error: ${error}`;
					return reject({
						key: GET_PAYMENT_TOKEN_COMPLETED,
						value: {
							error: errorMessage,
						},
					});
				}

				if (tokenResult.status === 'OK') {
					return resolve({
						key: GET_PAYMENT_TOKEN_COMPLETED,
						value: {
							squarePaymentMethod: tokenResult.token,
						},
					});
				}

				if (tokenResult.status === 'Cancel') {
					return reject({
						key: GET_PAYMENT_TOKEN_COMPLETED,
						value: {
							error: 'Payment cancelled',
						},
					});
				}

				let errorMessage = `Tokenization failed with status: ${tokenResult.status}`;
				if (tokenResult.errors) {
					errorMessage += ` and errors: ${JSON.stringify(
						tokenResult.errors,
					)}`;
				}

				return reject({
					key: GET_PAYMENT_TOKEN_COMPLETED,
					value: {
						error: errorMessage,
					},
				});
			});
		});

		await paymentMethod.tokenize(paymentData);
		return await promise;
	} catch (error) {
		if ('key' in error && 'value' in error) {
			return Promise.reject(error);
		}

		return Promise.reject({
			key: GENERAL_ERROR,
			value: {
				error: error.message,
			},
		});
	}
}

/**
 *
 */
async function tokenizeCard(paymentData) {
	try {
		const tokenResult = await paymentMethod.tokenize(paymentData);
		if (tokenResult.status === 'OK') {
			return {
				key: GET_PAYMENT_TOKEN_COMPLETED,
				value: {
					squarePaymentMethod: tokenResult.token,
				},
			};
		}
		let errorMessage = `Tokenization failed-status: ${tokenResult.status}`;
		if (tokenResult.errors) {
			errorMessage += ` and errors: ${JSON.stringify(
				tokenResult.errors,
			)}`;
		}
		return Promise.reject({
			key: GET_PAYMENT_TOKEN_COMPLETED,
			value: {
				error: errorMessage,
			},
		});
	} catch (error) {
		return Promise.reject({
			key: GENERAL_ERROR,
			value: { error: error.message },
		});
	}
}

/**
 * Get the payment token
 * @param paymentData
 * @returns {Promise<{key: string, value?: any}>} The payment token
 */
export async function getToken(paymentData) {
	if (paymentData.method === 'bank') {
		return await tokenizeAch(paymentData);
	}

	return await tokenizeCard(paymentData);
}

registerPaymentProcessor({
	name: 'Square',
	getToken,
	handleDomReady,
});
